// MACHINERY V2 (Stroje) — V4 shop-floor wall: the header's status counters
// (M2.14; split out of WallHeader.tsx by the 2026-08-24 exit-button ruling,
// which would otherwise have pushed that file past the project's 300-line cap).
//
// Rendered truth: desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png. Reference impl: desktop/machinery-v4-wall.jsx
// (V4WallHeader). NOTHING ABOUT THE COUNTERS CHANGED IN THE MOVE — the same
// specs, the same markup and the same reasoning, relocated verbatim.
//
// THE ALARM COUNTER RENDERS ONLY WHEN > 0 (the ticket says so, and the artboard
// shows it present, at 01). A permanent "00 V ALARME" on a hall TV is a
// standing red numeral that people stop reading; its absence is the signal.
//
// COUNTS COME FROM THE SAME SET THE TILES DO — WallHeader is handed
// countStates(<the machines the grid renders>, states). On a surface with no
// filter rail there is nothing to explain a discrepancy, so a header counting
// machines the grid below does not show would simply be wrong.
//
// SINCE THE M2.14 FIX PASS THAT SET IS THE WHOLE ACTIVE PARK: the wall renders
// `FleetPageData.flat`, the un-faceted base set the rail's own counts are
// computed over (useFleetPageData.ts:203, `countStates(flat, states)`,
// personally re-read at commit time). "Count what we render" and "count the
// plant" are now the same number; handed the FACETED `visibleFlat` they were
// not, and a header reading 01 V ALARME over a hall of five was self-consistent
// and false.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import StatusIcon from "../StatusIcon";
import { MachineryV2IconKind, MachineryV2StatusToken } from "../cardStates";

interface WallCounterSpec {
  /** Sum of these canonical states (M2.1 contract names). */
  states: string[];
  token: MachineryV2StatusToken;
  icon: MachineryV2IconKind;
  labelKey: string;
  /** Hide the counter entirely at zero (alarm only). */
  hideWhenZero?: boolean;
}

/**
 * The artboard's five counters, mapped onto the 9-state contract.
 *
 * `stale-alarm` is summed into the alarm counter ON PURPOSE: a machine whose
 * alarm reading is last-known is still a machine in alarm, and the wall's job
 * is to make someone walk over. The TILE keeps the distinction (it desaturates
 * and prints the last-known prefix); the counter does not need two numerals for
 * one problem.
 *
 * `unconnected`, `awaiting` and `stale` get NO counter, exactly as the artboard
 * has none: on a real fleet ~99% of machines are unconnected, so a sixth
 * numeral in the hundreds would dwarf the five that matter. All three are still
 * rendered as tiles below.
 */
const WALL_COUNTERS: readonly WallCounterSpec[] = [
  {
    states: ["alarm", "stale-alarm"],
    token: "--red",
    icon: "alert",
    labelKey: "myMachinery:v2.fleet.wall.cAlarm",
    hideWhenZero: true,
  },
  {
    states: ["running"],
    token: "--green",
    icon: "play",
    labelKey: "myMachinery:v2.fleet.wall.cRunning",
  },
  {
    states: ["idle"],
    token: "--amber",
    icon: "pause",
    labelKey: "myMachinery:v2.fleet.wall.cIdle",
  },
  {
    states: ["stopped"],
    token: "--muted",
    icon: "square",
    labelKey: "myMachinery:v2.fleet.wall.cStopped",
  },
  {
    states: ["offline"],
    token: "--faint",
    icon: "cloudoff",
    labelKey: "myMachinery:v2.fleet.wall.cOffline",
  },
];

const WallCounter: React.FC<{ n: number; label: string; spec: WallCounterSpec }> = ({
  n,
  label,
  spec,
}) => {
  const color = "var(" + spec.token + ")";
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
      {/* Zero-padded to two digits as the artboard prints it — padStart never
          truncates, so a three-digit fleet count still prints all three. */}
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 36,
          color,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {String(n).padStart(2, "0")}
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--ink-2)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          opacity: 0.75,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ color, display: "inline-flex" }}>
          <StatusIcon kind={spec.icon} size={11} />
        </span>
        {label}
      </span>
    </div>
  );
};

export interface WallCountersProps {
  /** countStates() over the machines the grid renders. */
  counts: Record<string, number>;
}

const WallCounters: React.FC<WallCountersProps> = ({ counts }) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      style={{
        display: "flex",
        gap: 22,
        rowGap: 8,
        flexWrap: "wrap",
        alignItems: "baseline",
      }}
    >
      {WALL_COUNTERS.map((spec) => {
        const n = spec.states.reduce((sum, s) => sum + (counts[s] || 0), 0);
        if (spec.hideWhenZero && n === 0) return null;
        return (
          <WallCounter key={spec.labelKey} n={n} label={translate(spec.labelKey)} spec={spec} />
        );
      })}
    </div>
  );
};

export default WallCounters;
